import type { ImageSegmenter, MPMask } from '@mediapipe/tasks-vision';

/**
 * Freistellen der Regie-Kamera: Person behalten, Hintergrund weichzeichnen
 * oder ersetzen.
 *
 * Das passiert hier beim Sender und nicht im Player, und dafuer gibt es einen
 * harten Grund: eine WebRTC-Videospur kann kein Alpha. Transparenz uebersteht
 * die Uebertragung nicht, der Ersatzhintergrund muss also *vor* dem Senden im
 * Bild sein. Praktischer Nebeneffekt: Zuschauer und RTMP-Ausgang bekommen das
 * Ergebnis ohne jeden Zusatzaufwand -- fuer sie ist es einfach ein Kamerabild.
 *
 * Der Preis ist die Rechenzeit: eine Maske pro Bild in einem KI-Modell. Auf
 * einem Regie-Laptop ist das machbar, auf dem Handy einer Athletin nicht --
 * deshalb bietet nur die Regie diese Funktion an.
 */

/** Vom Einrichtungsschritt `npm run effects:setup` dorthin gelegt. */
const WASM_PATH = '/mediapipe/wasm';
const MODEL_PATH = '/mediapipe/selfie_segmenter.tflite';

/** Grosszuegig gerechnet: mehr als 720p braucht kein Vereinsstream. */
const MAX_SIDE = 1280;

type VisionModule = typeof import('@mediapipe/tasks-vision');

let modulePromise: Promise<VisionModule> | null = null;

/**
 * Erst laden, wenn wirklich freigestellt wird. Das Paket bringt ein paar
 * hundert Kilobyte JavaScript und ueber 20 MB WASM mit -- die soll niemand
 * bezahlen, der nur zuschaut.
 */
const loadVision = (): Promise<VisionModule> => (modulePromise ??= import('@mediapipe/tasks-vision'));

export interface SegmentTarget {
  /** Bild, das hinter die Person kommt. Ohne Bild gilt `color`. */
  image: HTMLImageElement | null;
  /** Rueckfallfarbe, wenn kein Bild vorliegt. */
  color: string;
  /** Weichzeichnung des echten Hintergrunds in Pixeln (Modus 'blur'). */
  blur: number;
  /** Abdunkeln des Ersatzhintergrunds in Prozent -- wie auf der Buehne. */
  dim: number;
}

export interface SegmentStats {
  /** Tatsaechlich erreichte Bildrate -- die Regie soll sehen, was es kostet. */
  fps: number;
  /** Ob das Modell auf der Grafikkarte laeuft. */
  gpu: boolean;
}

/**
 * Laeuft die Segmentierung als Endlosschleife auf einem Canvas und gibt
 * dessen Stream heraus.
 */
export class BackgroundEffect {
  private segmenter: ImageSegmenter | null = null;
  private canvas = document.createElement('canvas');
  /** Zwischenstufe: das Kamerabild mit der Maske als Alphakanal. */
  private cut = document.createElement('canvas');
  /** Die Maske selbst, in der (kleinen) Groesse des Modells. */
  private mask = document.createElement('canvas');
  private maskData: ImageData | null = null;
  private frame: number | null = null;
  private stopped = false;
  private lastTimestamp = -1;
  private personIndex = 1;
  private frames = 0;
  private since = performance.now();
  private stats: SegmentStats = { fps: 0, gpu: false };

  private constructor(
    private video: HTMLVideoElement,
    private width: number,
    private height: number,
  ) {
    this.canvas.width = width;
    this.canvas.height = height;
    this.cut.width = width;
    this.cut.height = height;
  }

  /**
   * Baut die Kette auf. Wirft mit lesbarer Meldung, wenn Modell oder WASM
   * fehlen -- das ist der wahrscheinlichste Fehler und meint immer dasselbe:
   * `npm run effects:setup` wurde nicht gelaufen.
   */
  static async create(source: MediaStream): Promise<BackgroundEffect> {
    const track = source.getVideoTracks()[0];
    if (!track) throw new Error('Diese Quelle hat kein Bild.');

    const settings = track.getSettings();
    const scale = Math.min(1, MAX_SIDE / Math.max(settings.width ?? MAX_SIDE, settings.height ?? MAX_SIDE));
    const width = Math.round((settings.width ?? 1280) * scale);
    const height = Math.round((settings.height ?? 720) * scale);

    // Das Modell braucht ein Medienelement als Eingang, kein MediaStream.
    const video = document.createElement('video');
    video.srcObject = new MediaStream([track]);
    video.muted = true;
    video.playsInline = true;
    await video.play();

    const effect = new BackgroundEffect(video, width, height);
    await effect.init();
    return effect;
  }

  private async init() {
    const vision = await loadVision().catch(() => {
      throw new Error('Das Effekt-Paket fehlt — bitte `npm install` laufen lassen.');
    });

    const fileset = await vision.FilesetResolver.forVisionTasks(WASM_PATH).catch(() => {
      throw new Error('Die Effekt-Dateien fehlen — bitte `npm run effects:setup` laufen lassen.');
    });

    // GPU ist deutlich schneller; auf Rechnern ohne brauchbaren Treiber
    // scheitert sie aber, und dann ist die CPU besser als nichts.
    for (const delegate of ['GPU', 'CPU'] as const) {
      try {
        this.segmenter = await vision.ImageSegmenter.createFromOptions(fileset, {
          baseOptions: { modelAssetPath: MODEL_PATH, delegate },
          runningMode: 'VIDEO',
          outputCategoryMask: false,
          outputConfidenceMasks: true,
        });
        this.stats = { ...this.stats, gpu: delegate === 'GPU' };
        break;
      } catch (err) {
        if (delegate === 'CPU') {
          throw new Error(
            `Das Modell liesz sich nicht laden (${err instanceof Error ? err.message : 'unbekannt'}). ` +
              'Liegt web/public/mediapipe? Sonst `npm run effects:setup`.',
          );
        }
      }
    }

    /*
     * Welche Maske die Person ist, steht nicht fest -- das Modell liefert eine
     * pro Kategorie. Statt einen Index zu raten, fragen wir nach den Namen:
     * alles auszer „background" ist die Person.
     */
    const labels = this.segmenter?.getLabels() ?? [];
    const found = labels.findIndex((label) => !/background|hintergrund/i.test(label));
    this.personIndex = found >= 0 ? found : Math.max(0, labels.length - 1);
  }

  get size() {
    return { width: this.width, height: this.height };
  }

  getStats(): SegmentStats {
    return this.stats;
  }

  /** Der Stream, der statt der rohen Kamera gesendet wird. */
  captureStream(fps: number): MediaStream {
    return this.canvas.captureStream(fps);
  }

  /** Startet die Schleife. `read` wird pro Bild gefragt, damit ein Wechsel
   *  von Modus oder Hintergrund sofort greift, ohne Neuaufbau. */
  start(read: () => { mode: 'blur' | 'backdrop'; target: SegmentTarget }) {
    const ctx = this.canvas.getContext('2d');
    const cutCtx = this.cut.getContext('2d');
    const maskCtx = this.mask.getContext('2d');
    if (!ctx || !cutCtx || !maskCtx) throw new Error('Dieser Browser kann kein 2D-Canvas.');

    const tick = () => {
      if (this.stopped) return;
      this.frame = requestAnimationFrame(tick);

      const segmenter = this.segmenter;
      if (!segmenter || this.video.readyState < 2) return;

      // Das Modell verlangt streng steigende Zeitstempel.
      const timestamp = Math.max(this.lastTimestamp + 1, Math.round(performance.now()));
      this.lastTimestamp = timestamp;

      const { mode, target } = read();

      segmenter.segmentForVideo(this.video, timestamp, (result) => {
        const mask = result.confidenceMasks?.[this.personIndex] ?? result.confidenceMasks?.[0];
        if (mask) this.compose(ctx, cutCtx, maskCtx, mask, mode, target);
        result.close();
      });

      this.frames += 1;
      const elapsed = performance.now() - this.since;
      if (elapsed >= 1000) {
        this.stats = { ...this.stats, fps: Math.round((this.frames * 1000) / elapsed) };
        this.frames = 0;
        this.since = performance.now();
      }
    };

    this.frame = requestAnimationFrame(tick);
  }

  /**
   * Setzt ein Bild zusammen: erst der Hintergrund, dann die Person darueber.
   *
   * Die Person entsteht ueber `destination-in`: das Kamerabild wird auf einem
   * eigenen Canvas gezeichnet und dann mit der Maske als Alphakanal
   * beschnitten. Die Maske ist kleiner als das Bild (das Modell rechnet
   * intern in niedriger Aufloesung) -- `drawImage` skaliert sie hoch und
   * glaettet dabei, was die Kanten weich macht.
   */
  private compose(
    ctx: CanvasRenderingContext2D,
    cutCtx: CanvasRenderingContext2D,
    maskCtx: CanvasRenderingContext2D,
    mask: MPMask,
    mode: 'blur' | 'backdrop',
    target: SegmentTarget,
  ) {
    const { width, height } = this;

    /* 1. Hintergrund */
    ctx.save();
    ctx.clearRect(0, 0, width, height);
    if (mode === 'blur') {
      ctx.filter = `blur(${Math.max(2, target.blur)}px)`;
      // Etwas ueber den Rand zeichnen, sonst hellt die Weichzeichnung die
      // Bildkanten auf.
      const pad = Math.max(2, target.blur) * 2;
      ctx.drawImage(this.video, -pad, -pad, width + pad * 2, height + pad * 2);
    } else if (target.image?.complete && target.image.naturalWidth > 0) {
      drawCover(ctx, target.image, width, height);
      if (target.blur > 0) {
        // Ein zweiter, weichgezeichneter Durchgang -- `filter` gilt nur beim
        // Zeichnen, ein nachtraegliches Weichzeichnen gibt es nicht.
        ctx.filter = `blur(${target.blur}px)`;
        drawCover(ctx, target.image, width, height);
      }
    } else {
      ctx.fillStyle = target.color;
      ctx.fillRect(0, 0, width, height);
    }
    ctx.restore();

    if (mode === 'backdrop' && target.dim > 0) {
      ctx.save();
      ctx.fillStyle = `rgba(0,0,0,${Math.min(0.85, target.dim / 100)})`;
      ctx.fillRect(0, 0, width, height);
      ctx.restore();
    }

    /* 2. Maske in einen Alphakanal umschreiben */
    if (this.mask.width !== mask.width || this.mask.height !== mask.height) {
      this.mask.width = mask.width;
      this.mask.height = mask.height;
      this.maskData = maskCtx.createImageData(mask.width, mask.height);
    }
    const image = this.maskData!;
    const confidence = mask.getAsFloat32Array();
    const pixels = image.data;
    for (let i = 0, p = 3; i < confidence.length; i++, p += 4) {
      pixels[p] = confidence[i] * 255;
    }
    maskCtx.putImageData(image, 0, 0);

    /* 3. Person ausstanzen und darueber setzen */
    cutCtx.save();
    cutCtx.clearRect(0, 0, width, height);
    cutCtx.drawImage(this.video, 0, 0, width, height);
    cutCtx.globalCompositeOperation = 'destination-in';
    cutCtx.drawImage(this.mask, 0, 0, width, height);
    cutCtx.restore();

    ctx.drawImage(this.cut, 0, 0);
  }

  stop() {
    this.stopped = true;
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
    this.segmenter?.close();
    this.segmenter = null;
    this.video.srcObject = null;
  }
}

/** Zeichnet ein Bild formatfuellend, ohne es zu verzerren. */
function drawCover(ctx: CanvasRenderingContext2D, image: HTMLImageElement, width: number, height: number) {
  const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
  const w = image.naturalWidth * scale;
  const h = image.naturalHeight * scale;
  ctx.drawImage(image, (width - w) / 2, (height - h) / 2, w, h);
}

/** Laedt ein Hintergrundbild vor -- ohne `complete` waere es im Canvas leer. */
export function preloadImage(src: string | null): Promise<HTMLImageElement | null> {
  if (!src) return Promise.resolve(null);
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = src;
  });
}
